import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MapPin, Navigation, Star, Loader2, AlertCircle, Car, ArrowLeft, Search } from 'lucide-react';
import AddressAutocomplete from '@/components/AddressAutocomplete';

// Normaliza o prestador do endpoint /providers/ranked para um formato estável.
// Preserva o id original (provider_id -> id) para uso futuro no hand-off (Pass 9C).
const normalizeProvider = (p) => ({
  id: p.provider_id,
  name: p.name,
  address: p.address || null,
  rating: typeof p.rating_average === 'number' ? p.rating_average : null,
  distanceKm: typeof p.distance_km === 'number' ? p.distance_km : null,
  score: typeof p.score === 'number' ? p.score : null,
  completed: typeof p.completed_services === 'number' ? p.completed_services : null,
});

export default function ClientProviders() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [addressText, setAddressText] = useState('');
  const [coords, setCoords] = useState({ latitude: null, longitude: null });
  const [gettingLocation, setGettingLocation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(null);

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
    }
  }, [user, navigate]);

  // Busca de descoberta (SEM data/hora) — GET /providers/ranked
  const searchProviders = async (latitude, longitude) => {
    setLoading(true);
    setError(false);
    setSearched(true);
    setProviders([]);
    try {
      const res = await axios.get(`${API}/providers/ranked`, {
        params: { latitude, longitude, max_distance_km: 50, limit: 20 },
      });
      const list = Array.isArray(res.data?.providers) ? res.data.providers : [];
      setProviders(list.map(normalizeProvider));
    } catch (e) {
      setError(true);
      toast.error('Não foi possível buscar os lava-jatos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Endereço selecionado no autocomplete -> coords + busca
  const handleAddressSelect = (selected) => {
    if (selected?.latitude && selected?.longitude) {
      setAddressText(selected.address || addressText);
      setCoords({ latitude: selected.latitude, longitude: selected.longitude });
      searchProviders(selected.latitude, selected.longitude);
    }
  };

  // Localização atual — implementação mínima e independente (não usa Schedule.js)
  const useCurrentLocation = async () => {
    setGettingLocation(true);
    const onSuccess = (latitude, longitude) => {
      setCoords({ latitude, longitude });
      setAddressText('Localização atual');
      setGettingLocation(false);
      searchProviders(latitude, longitude);
    };
    try {
      if (Capacitor.isNativePlatform()) {
        let perm = await Geolocation.checkPermissions();
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
          perm = await Geolocation.requestPermissions({ permissions: ['location'] });
        }
        if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
          setGettingLocation(false);
          toast.error('Permissão de localização negada.');
          return;
        }
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
        onSuccess(pos.coords.latitude, pos.coords.longitude);
        return;
      }
      if (!navigator.geolocation) {
        setGettingLocation(false);
        toast.error('Geolocalização não suportada pelo navegador.');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => onSuccess(position.coords.latitude, position.coords.longitude),
        () => {
          setGettingLocation(false);
          toast.error('Não foi possível obter sua localização.');
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } catch (e) {
      setGettingLocation(false);
      toast.error('Erro ao obter localização.');
    }
  };

  // Pass 9C: hand-off — leva o prestador selecionado (dados reais do /providers/ranked, id
  // normalizado) e a localização usada na descoberta para o fluxo de agendamento.
  // NÃO cria reserva nem chama endpoint de agendamento — apenas navega com router state.
  const handleSelectProvider = (provider) => {
    setSelectedProviderId(provider.id);
    navigate('/client/schedule', {
      state: {
        preSelectedProvider: provider,
        location: {
          latitude: coords.latitude,
          longitude: coords.longitude,
          address: addressText,
          method: 'manual'
        }
      }
    });
  };

  const renderRating = (rating) => {
    if (rating === null || rating <= 0) {
      return <span className="text-xs">Novo</span>;
    }
    return (
      <>
        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
        <span>{rating.toFixed(1)}</span>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="page-providers">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')} data-testid="providers-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 p-2 rounded-full">
              <MapPin className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold" data-testid="providers-title">Lava-jatos</h1>
              <p className="text-xs text-muted-foreground">Encontre lava-jatos perto de você</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Localização */}
        <Card className="p-4 space-y-3">
          <Button
            className="w-full"
            onClick={useCurrentLocation}
            disabled={gettingLocation}
            data-testid="providers-use-gps-btn"
          >
            {gettingLocation ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Obtendo localização...</>
            ) : (
              <><Navigation className="w-4 h-4 mr-2" /> Usar localização atual</>
            )}
          </Button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-muted-foreground">OU</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Informar endereço</p>
            <AddressAutocomplete
              value={addressText}
              onChange={setAddressText}
              onSelect={handleAddressSelect}
              placeholder="Digite o endereço (ex: Avenida Paulista, São Paulo)"
            />
          </div>
        </Card>

        {/* Resultados */}
        {loading && (
          <div className="text-center py-10" data-testid="providers-loading">
            <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin mb-3" />
            <p className="text-muted-foreground">Buscando lava-jatos...</p>
          </div>
        )}

        {!loading && error && (
          <Card className="p-6 text-center bg-red-50" data-testid="providers-error">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3" />
            <p className="text-red-700 font-medium mb-3">Erro ao buscar lava-jatos.</p>
            {coords.latitude && (
              <Button variant="outline" onClick={() => searchProviders(coords.latitude, coords.longitude)}>
                <Search className="w-4 h-4 mr-2" /> Tentar novamente
              </Button>
            )}
          </Card>
        )}

        {!loading && !error && searched && providers.length === 0 && (
          <Card className="p-6 text-center bg-slate-50" data-testid="providers-empty">
            <AlertCircle className="w-10 h-10 mx-auto text-yellow-500 mb-3" />
            <p className="text-muted-foreground">Nenhum lava-jato encontrado nesta região.</p>
          </Card>
        )}

        {!loading && !error && providers.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1" data-testid="providers-results-header">
              <p className="text-sm font-semibold text-slate-700">
                {providers.length} {providers.length === 1 ? 'lava-jato encontrado' : 'lava-jatos encontrados'}
              </p>
              {addressText && (
                <span className="text-xs text-muted-foreground flex items-center gap-1 max-w-[55%]">
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span className="truncate">{addressText}</span>
                </span>
              )}
            </div>
            {providers.map((provider) => (
              <Card
                key={provider.id}
                className={`p-4 transition-colors border-2 ${selectedProviderId === provider.id ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-slate-50'}`}
                data-testid={`provider-card-${provider.id}`}
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-slate-600 shrink-0" />
                      <h3 className="font-semibold text-base truncate">{provider.name}</h3>
                    </div>
                    {provider.address?.full_address && (
                      <p className="text-sm text-muted-foreground mt-1 flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                        <span className="truncate">{provider.address.full_address}</span>
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">{renderRating(provider.rating)}</span>
                      {provider.completed !== null && provider.completed > 0 && (
                        <>
                          <span>•</span>
                          <span>{provider.completed} {provider.completed === 1 ? 'serviço' : 'serviços'}</span>
                        </>
                      )}
                      {provider.distanceKm !== null && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {provider.distanceKm} km
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleSelectProvider(provider)}
                    data-testid={`select-provider-${provider.id}`}
                  >
                    Selecionar
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
